/** Display-time child anonymization.
 *
 *  Renders children by minimal-unique initials ("Madison" → "M.", or
 *  "Mad."/"Mas." when two children collide on the first letter) so a shared
 *  reading doesn't carry the kids' names. This is a NARRATIVE + LABELS
 *  transform only: it is applied to synthesis prose and people labels, never
 *  to quoted evidence — corpus message bodies stay verbatim so the
 *  evidentiary record an attorney relies on is unchanged. The transform is
 *  reversible (a display toggle); nothing is re-run or rewritten on disk.
 */

export interface AnonChild {
  /** Canonical first name (e.g. "Madison"). */
  name: string;
  /** Nicknames the user linked to this child (e.g. ["Maddy"]). All collapse
   *  to the canonical child's token. */
  aliases?: string[];
}

function firstWord(n: string): string {
  return (n ?? "").trim().split(/\s+/)[0] ?? "";
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Shortest case-insensitive prefix that is unique within `names`, formatted
 *  as a leading-capital token + ".". Non-colliding names yield a single
 *  letter ("M."); collisions grow until unique ("Mad." vs "Mas."). Genuinely
 *  identical names fall back to the full name + ".". */
function uniquePrefixes(names: string[]): string[] {
  const lower = names.map((n) => n.toLowerCase());
  return lower.map((me, i) => {
    if (!me) return "";
    let len = 1;
    while (
      len < me.length &&
      lower.some((o, j) => j !== i && o.slice(0, len) === me.slice(0, len))
    ) {
      len++;
    }
    const p = me.slice(0, len);
    return p.charAt(0).toUpperCase() + p.slice(1) + ".";
  });
}

/** Build a first-name → token map for a set of children. Each child and any
 *  of its aliases maps to the canonical child's token, so "Maddy" and
 *  "Madison" both render as the same initial. Keyed by lowercased first name. */
export function childInitialsMap(children: AnonChild[]): Map<string, string> {
  const canonicalFirsts = children.map((c) => firstWord(c.name));
  const tokens = uniquePrefixes(canonicalFirsts);
  const map = new Map<string, string>();
  children.forEach((c, i) => {
    const token = tokens[i];
    if (!token) return;
    for (const n of [c.name, ...(c.aliases ?? [])]) {
      const f = firstWord(n).toLowerCase();
      if (f) map.set(f, token);
    }
  });
  return map;
}

/** Build the AnonChild list for a case from its confirmed children + the
 *  wizard-confirmed nickname map (alias → canonical name, as persisted at
 *  meta.json top-level). Each child collects the aliases whose canonical IS
 *  that child, so a nickname ("Maddy") masks to the same initial as the name
 *  ("Madison" → "M.", "Maddy" → "M."). Canonical match is case-insensitive. */
export function childrenFromMeta(
  confirmedChildren: ReadonlyArray<{ name: string }> | null | undefined,
  nicknameMap: Record<string, string> | null | undefined,
): AnonChild[] {
  const map = nicknameMap ?? {};
  const entries = Object.entries(map);
  return (confirmedChildren ?? [])
    .map((c) => (c?.name ?? "").trim())
    .filter(Boolean)
    .map((name) => {
      const lc = name.toLowerCase();
      const aliases = entries
        .filter(([, canon]) => (canon ?? "").trim().toLowerCase() === lc)
        .map(([alias]) => alias.trim())
        .filter(Boolean);
      return aliases.length ? { name, aliases } : { name };
    });
}

/** Replace whole-word occurrences of any mapped child name with its token.
 *  Single-pass alternation (longest name first) so already-substituted tokens
 *  are never re-scanned; possessives keep their suffix ("Madison's" → "M.'s").
 *  Word boundaries are Unicode-aware (JS `\b` is ASCII-only, so "Zoë" never
 *  matched — review 2026-09-08), and only a capitalised occurrence is a
 *  name: a child called Will, May, Grace or Rose must not rewrite "I will
 *  pick her up" in a quoted message. Returns `text` unchanged when there's
 *  nothing to mask. Since the DOCX export masks quoted evidence too (the
 *  packet says so on its cover), the capital rule is what keeps the parties'
 *  own words intact. */
export function maskChildNames(
  text: string,
  map: Map<string, string>,
): string {
  if (!text || map.size === 0) return text;
  const names = [...map.keys()]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  const re = new RegExp(
    `(?<![\\p{L}\\p{N}_])(${names.join("|")})(?![\\p{L}\\p{N}_])`,
    "giu",
  );
  return text.replace(re, (m) => {
    const first = m[0] ?? "";
    if (first !== first.toUpperCase() || first === first.toLowerCase()) return m;
    return map.get(m.toLowerCase()) ?? m;
  });
}

/** Deep-clone `value`, masking every string leaf with `maskChildNames`. Object
 *  keys and Map keys are preserved; only values are transformed. Lets a whole
 *  narrative tree (e.g. a RunFiles' `packet` + `pattern_details` + `provenance`)
 *  be anonymized in one pass with no field to forget — and since masking is a
 *  no-op on any string that doesn't contain a child name, it's safe to run over
 *  the entire tree. The DOCX export also runs it over the cited corpus (the
 *  packet's cover says names are shown as initials throughout, quoted
 *  messages included); the on-screen viewer never does. */
export function deepMaskChildNames<T>(value: T, map: Map<string, string>): T {
  if (map.size === 0) return value;
  const walk = (v: unknown): unknown => {
    if (typeof v === "string") return maskChildNames(v, map);
    if (Array.isArray(v)) return v.map(walk);
    if (v instanceof Map) {
      const out = new Map();
      for (const [k, val] of v) out.set(k, walk(val));
      return out;
    }
    if (v && typeof v === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v)) out[k] = walk(val);
      return out;
    }
    return v;
  };
  return walk(value) as T;
}

// ---------------------------------------------------------------------------
// Shared parties/children/nickname contract helpers.
//
// These are imported by BOTH the desktop app and the cloud frontend (the cloud
// reaches into this file via the `@app/*` path alias) so the two products emit
// byte-identical `prompt_vars.context` and write the same `nickname_map`. This
// module is intentionally Tauri-free — it is bundled into the browser-only
// cloud build, so it must never import `invoke`, the Tauri plugins, or
// `@/lib/tauri`. Persistence is the host's job; these are pure functions.
// See docs/superpowers/specs/2026-06-05-cloud-desktop-intake-unification-design.md
// ---------------------------------------------------------------------------

/** Flat alias→canonical (the meta.json `nickname_map` shape) → canonical→aliases[].
 *  Empty/missing canonicals or aliases are dropped; both are trimmed. */
export function aliasesByCanonical(
  nicknameMap: Record<string, string> | null | undefined,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [aliasRaw, canonRaw] of Object.entries(nicknameMap ?? {})) {
    const alias = (aliasRaw ?? "").trim();
    const canon = (canonRaw ?? "").trim();
    if (!alias || !canon) continue;
    (out[canon] ??= []).push(alias);
  }
  return out;
}

/** canonical→aliases[] → flat alias→canonical (the meta.json `nickname_map`
 *  shape). Empty canonicals/aliases dropped; both trimmed. If two canonicals
 *  claim the same alias, the later one wins (last-write, matching plain object
 *  assignment) — the editor UX prevents that case. */
export function nicknameMapFromAliases(
  aliasesByChild: Record<string, string[]> | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [canonRaw, aliases] of Object.entries(aliasesByChild ?? {})) {
    const canon = (canonRaw ?? "").trim();
    if (!canon) continue;
    for (const aliasRaw of aliases ?? []) {
      const alias = (aliasRaw ?? "").trim();
      if (alias) out[alias] = canon;
    }
  }
  return out;
}

const CONTEXT_NUM_WORDS = [
  "zero", "one", "two", "three", "four",
  "five", "six", "seven", "eight", "nine",
];

/** Oxford-join a list of names ("A" / "A and B" / "A, B, and C"). */
function joinNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/** THE single source of the `prompt_vars.context` framing sentence, shared by
 *  desktop + cloud so both emit identical wording. `childNames` are the
 *  confirmed children's canonical names (display order); `aliasesByChild` maps
 *  a canonical first name → its linked aliases, rendered inline as
 *  "(also called …)". Zero children → "their co-parenting relationship".
 *
 *  Lifted verbatim from the cloud's former `childContextSentence` (the chosen
 *  canonical wording). A future structured signal (e.g. a filing date) would be
 *  a NEW optional parameter or function, not a change to this signature —
 *  derivation is extensible, but no such field exists yet.
 *
 *  PARITY NOTE: the no-alias output ("their child Madison", "their co-parenting
 *  relationship", …) must keep matching the cloud server's
 *  aurora_cloud.entity_classifier.build_context_sentence (Python), which still
 *  produces the initial classify phrase. Keep the two in sync. */
export function deriveContext(
  childNames: string[],
  aliasesByChild?: Record<string, string[]>,
): string {
  const firsts = childNames
    .map((n) => n.trim().split(/\s+/)[0])
    .filter((f) => f);
  if (firsts.length === 0) return "their co-parenting relationship";
  // Attach "(also called X)" to a child whose nicknames the user linked.
  const tok = (first: string) => {
    const aka = (aliasesByChild?.[first] ?? [])
      .map((a) => a.trim().split(/\s+/)[0])
      .filter(Boolean);
    return aka.length ? `${first} (also called ${joinNames(aka)})` : first;
  };
  const toks = firsts.map(tok);
  if (toks.length === 1) return `their child ${toks[0]}`;
  if (toks.length === 2)
    return `their two children ${toks[0]} and ${toks[1]}`;
  const word =
    firsts.length < CONTEXT_NUM_WORDS.length
      ? CONTEXT_NUM_WORDS[firsts.length]
      : String(firsts.length);
  const head = toks.slice(0, -1).join(", ");
  return `their ${word} children, ${head}, and ${toks[toks.length - 1]}`;
}
